import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories, type Category } from "@/data/categories";

export default function CategoryDropdown() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Category | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-md">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 rounded-full border border-background-300/70 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-800 cursor-pointer transition hover:border-primary-400"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-3 truncate">
          {selected && (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md">
              <img
                src={selected.image}
                alt={t(`cat.${selected.key}`)}
                className="h-full w-full object-cover object-top"
              />
            </span>
          )}
          <span className="truncate">
            {selected ? t(`cat.${selected.key}`) : t("dropdown.placeholder")}
          </span>
        </span>
        <i
          className={`ri-arrow-down-s-line text-base transition ${open ? "rotate-180" : ""}`}
        ></i>
      </button>

      {open && (
        <div className="absolute z-20 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1">
          {categories.map((cat) => (
            <button
              key={cat.key}
              type="button"
              onClick={() => {
                setSelected(cat);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm whitespace-nowrap cursor-pointer transition hover:bg-primary-50 ${
                selected?.key === cat.key
                  ? "font-semibold text-primary-700"
                  : "text-foreground-700"
              }`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md">
                <img
                  src={cat.image}
                  alt={t(`cat.${cat.key}`)}
                  className="h-full w-full object-cover object-top"
                />
              </span>
              <span className="flex-1 truncate">{t(`cat.${cat.key}`)}</span>
              {selected?.key === cat.key && (
                <i className="ri-check-line text-primary-600"></i>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3">
        {selected ? (
          <a
            href={selected.url}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-primary-600"
          >
            {t("dropdown.go")}
            <i className="ri-arrow-right-up-line"></i>
          </a>
        ) : (
          <span className="inline-flex items-center gap-2 rounded-full bg-background-200 px-6 py-3 text-sm font-semibold text-foreground-400 whitespace-nowrap cursor-not-allowed">
            {t("dropdown.go")}
          </span>
        )}
      </div>
    </div>
  );
}